import React, { FC, ReactElement, useState, useEffect, useRef } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
// Component
import InputField from '../form/input';
// Hooks
import { useDebounce } from '../hook/useDebounce';
import SvgIcon from '../icons/SvgIcon';
import { IconAxel } from '../../resources/images';

interface InfoBoxProps {
    label: string;
    placeholder?: string;
    minCharInput: number;
    intervalCharInput: number;
    intervalTimeInput: number;
    disabled?: boolean;
    withIcon?: boolean;
    onFocusFunction?: () => any;
    searchAction: (input: string) => void;
    // Nouvelles props pour éviter les re-renders
    initialValue?: string;
    key?: string | number; // Pour forcer le re-mount si nécessaire
}

const SearchInput: FC<InfoBoxProps> = (props): ReactElement => {
    const {
        searchAction,
        minCharInput,
        intervalCharInput,
        intervalTimeInput,
        label,
        placeholder,
        disabled = false,
        withIcon = false,
        onFocusFunction = () => {},
        initialValue = '',
    } = props;

    const classes = useStyles();

    const [input, setInput] = useState(initialValue);

    // Utiliser useRef pour éviter les re-renders lors des callbacks
    const searchActionRef = useRef(searchAction);
    searchActionRef.current = searchAction;

    const canSearch: boolean = input?.length >= minCharInput;
    const intervalSearch: boolean = input?.length % intervalCharInput === 0;

    const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        setInput(value);
    };

    useDebounce(
        () => {
            if (canSearch && !intervalSearch) {
                // Utiliser la ref pour éviter les dépendances qui causent des re-renders
                searchActionRef.current(input);
            }
        },
        intervalTimeInput,
        [input], // Seulement input comme dépendance
    );

    useEffect(() => {
        if (intervalSearch) {
            searchAction(input);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input]);

    return (
        <InputField
            id="textfield0"
            onFocus={onFocusFunction}
            onChange={onChangeInput}
            name="search-field"
            autoComplete="off"
            disabled={disabled}
            value={input}
            label={label}
            placeholder={placeholder}
            className={placeholder ? classes.inputPlaceholder : ''}
            startAdornment={
                withIcon ? (
                    <SvgIcon
                        icon={IconAxel.Search}
                        size="mediumSmall"
                        className={placeholder ? classes.searchIconPlaceholder : classes.searchIcon}
                    />
                ) : null
            }
        />
    );
};

const useStyles = makeStyles(() =>
    createStyles({
        searchIcon: {
            marginTop: 15,
            marginLeft: 2,
        },
        inputPlaceholder: {
            '& .MuiInputBase-input': {
                paddingTop: 16,
                paddingBottom: 17,
            },
        },
        searchIconPlaceholder: {
            marginLeft: 2,
        },
    }),
);

export default SearchInput;
